<script setup lang="ts">
/**
 * MY — Beneficial Owner Letters Appendix A/B/C (M32109) 문서-뷰 패널.
 * 설문으로 Appendix 선택 → 해당 문서만 플랫 패키지.
 * Corporate/Individual 동시 선택 시 ba02_/ba03_/ba04_/ba05_ 로 필드·criteria 분리.
 */
import { ref, reactive, computed, provide, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import {
  BO_APPENDIX_DOCS, BO_APPENDIX_DOC_CODES, labelize, typeOf, isOptional,
  adaptFormDataForDoc,
  stripDocKeyPrefix,
  docKeyPrefix,
  comboOptionsOf,
  resolveBoRace,
  resolveBoGender,
  resolveBoNationality,
  BO_PARTICULARS, BO_PCT, SM_PARTICULARS,
  type BoAppendixDocCode,
} from './boAppendix/boAppendixFields';
import { resolveIncludedAppendixCodes, normalizeAppendixSurvey, type BoAppendixSurvey } from './boAppendix/boAppendixSurvey';
import { BO_APPENDIX_DOC_CTX } from './boAppendix/boAppendixContext';
import {
  buildBoAppendixVariables,
  emptyBoAppendixState,
  ensureCriteriaDoc,
  criteriaFlagsForDoc,
  smReasonFlagsFromId,
  composeBoAppendixAddressLine,
  toBoAppendixAddressParts,
  type BoAppendixState,
} from '../mappers/boAppendixMapper';
import { parseMalaysianAddress, isEmptyMyAddress } from '@/shared/utils/malaysianAddress';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import AddressInputModal from './cdd/AddressInputModal.vue';
import BoAppendixPackageDoc from './boAppendix/BoAppendixPackageDoc.vue';
import {
  emptyBoAppendixCriteria,
  formatBoDateDisplay,
  isCorporateBoRow,
  isIndividualBoRow,
  mapBoRowToAppendixParticulars,
  mapBoRowToCriteriaAndPercents,
  parseBoJsonPayload,
} from './boAppendix/mapBoJsonToAppendix';
import { formatMyAddressLine } from '@/shared/utils/malaysianAddress';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  editTaskId?: number | null;
  studio?: boolean;
  appliedAddress?: { key: string; line: string; town: string; state: string; postcode: string; country: string; seq: number } | null;
  pendingPick?: { value: string; seq: number } | null;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const entityCd = computed(() => String(props.entityData?.entityCd || props.entityData?.icpCd || '').trim());

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());

const state = reactive<BoAppendixState>(emptyBoAppendixState());

const activeKey = ref('');
const inputKey = ref('');
const inputText = ref('');
const dateMenu = ref(false);

function setTargetSigner(
  docCode: BoAppendixDocCode,
  person: { name: string } | null,
  title: string,
  designationKey: string,
  force = false,
) {
  if (!person?.name) {
    if (force) {
      state.signers[docCode] = [];
      state.values[designationKey] = '';
    }
    return;
  }
  const cur = String(state.signers[docCode]?.[0]?.name || '').trim();
  if (!force && cur && cur === person.name) {
    if (!String(state.values[designationKey] || '').trim()) state.values[designationKey] = title;
    return;
  }
  state.signers[docCode] = [{
    id: `tgt-${docCode}-${person.name}`,
    name: person.name,
    title,
    role: 'recipient',
    showTitle: true,
    _signerLayout: 'box',
  }];
  state.values[designationKey] = title;
}

function syncPersonToSigners(fieldKey: string, force = true) {
  const name = String(state.values[fieldKey] || '').trim();
  if (!name) return;
  const person = { name };
  // Appendix A — 본인이 BO 로 선언 → 싸인대상 = BO
  if (fieldKey === 'bo_full_name') {
    setTargetSigner('BA01', person, 'Beneficial Owner', 'signer_designation_BA01', force);
  }
  // Appendix B Corporate — 법인 명의 서명란(member)
  if (fieldKey === 'ba02_member_name') {
    setTargetSigner('BA02', person, 'Authorised Signatory', 'signer_designation_BA02', force);
  }
  // Appendix C Corporate — Signers = Director(s). member_name 은 "For and on behalf of" 회사명만.
  // Appendix C Individual — Senior Management 본인 서명
  if (fieldKey === 'ba05_sm_full_name') {
    setTargetSigner('BA05', person, 'Senior Management', 'signer_designation_BA05', force);
  }
}

/** person 필드 키 → 같은 문서 scope 의 관련 필드 키 */
function relatedKey(personKey: string, base: string): string {
  const m = personKey.match(/^(ba0[2-5]_)/i);
  if (m) return `${m[1]}${base}`;
  return base;
}

function setFieldValue(key: string, val: string) {
  state.values[key] = val;
  // 주소 필드를 문서에 직접 타이핑해도 Register/Contact 용 구조화 주소가 따라가도록 동기화
  if (typeOf(key) === 'address') {
    if (!state.addresses) state.addresses = {};
    const trimmed = String(val || '').trim();
    if (!trimmed) {
      delete state.addresses[key];
    } else {
      const parsed = parseMalaysianAddress(trimmed);
      if (!isEmptyMyAddress(parsed)) {
        state.addresses[key] = toBoAppendixAddressParts({
          street: parsed.street || trimmed,
          town: parsed.town,
          state: parsed.state,
          postcode: parsed.postcode,
          country: parsed.country,
        });
      } else {
        state.addresses[key] = toBoAppendixAddressParts({ line: trimmed });
      }
    }
  }
  const base = stripDocKeyPrefix(key);
  if (/^(bo_full_name|sm_full_name|member_name)$/.test(base)) {
    syncPersonToSigners(key, true);
  }
}

function activate(key: string) {
  activeKey.value = key;
  const t = typeOf(key);
  if (t === 'combo') {
    // Race/Gender — 인라인 콤보만 사용 (상단 입력바 불필요)
    inputKey.value = '';
    return;
  }
  if (t === 'person' || t === 'entity') {
    const base = stripDocKeyPrefix(key);
    let defaultRole: string | null = null;
    let memberEntityKind: 'corporate' | 'individual' | undefined;
    // Appendix Corporate(ba02/ba04) / Individual(ba03/ba05) — Member 역할 선택 시에도 필터 유지
    let docEntityKind: 'corporate' | 'individual' | undefined;
    if (key.startsWith('ba02_') || key.startsWith('ba04_')) docEntityKind = 'corporate';
    else if (key.startsWith('ba03_') || key.startsWith('ba05_')) docEntityKind = 'individual';

    if (base === 'bo_full_name') {
      if (key.startsWith('ba02_') || key.startsWith('ba03_')) {
        // Appendix B = 신규 BO 지정 → Member 목록
        defaultRole = 'SHAREHOLDER';
        if (key.startsWith('ba02_')) memberEntityKind = 'corporate';
        else memberEntityKind = 'individual';
      } else {
        defaultRole = 'BENEFICIAL_OWNER';
      }
    } else if (base === 'member_name') {
      // Corporate member ("For and on behalf of") — 법인만
      defaultRole = 'SHAREHOLDER';
      memberEntityKind = docEntityKind === 'individual' ? 'individual' : 'corporate';
    } else if (base === 'sm_full_name') {
      // SM 인적은 개인(임원). Member 역할로 바꾸면 docEntityKind 로 법인/개인 필터.
      defaultRole = 'DIRECTOR';
    }
    emit('request-person-lookup', {
      fieldGroup: 'bo-appendix',
      key,
      defaultRole,
      lookupType: 'officer',
      ...(memberEntityKind ? { memberEntityKind } : {}),
      ...(docEntityKind ? { docEntityKind } : {}),
      // Member(SHAREHOLDER) lookup 일 때만 CATEGORY_CD 필터
      ...(defaultRole === 'SHAREHOLDER' && (memberEntityKind || docEntityKind)
        ? {
          contactCategoryCd:
            (memberEntityKind || docEntityKind) === 'corporate' ? 'B09001' : 'B09002',
        }
        : {}),
    });
    inputKey.value = '';
  } else {
    inputKey.value = key;
    inputText.value = state.values[key] || '';
    if (t === 'date') dateMenu.value = true;
  }
}
function onPickDate(d: any) {
  const date = Array.isArray(d) ? d[0] : d;
  if (!date) return;
  const dt = new Date(date);
  inputText.value = Number.isNaN(dt.getTime())
    ? String(date)
    : `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  dateMenu.value = false;
  commitInput();
}
function commitInput() {
  if (inputKey.value) setFieldValue(inputKey.value, inputText.value.trim());
  inputKey.value = ''; activeKey.value = '';
}
function skipInput() { inputKey.value = ''; activeKey.value = ''; }

function flatAddr(v: any): string {
  return formatMyAddressLine(String(v ?? ''));
}

const pendingPickValue = ref('');
watch(() => props.pendingPick, (p) => { pendingPickValue.value = p?.value || ''; });
function placePick(key: string): boolean {
  if (!pendingPickValue.value) return false;
  setFieldValue(key, pendingPickValue.value);
  pendingPickValue.value = '';
  return true;
}

const addrModal = ref(false);
const addrKey = ref('');
const addrLabel = ref('');
const addrInitial = ref<{ line?: string; town?: string; state?: string; postcode?: string; country?: string }>({});
function openAddress(key: string) {
  if (props.studio) { addrKey.value = key; emit('request-address-input', key); return; }
  addrKey.value = key;
  addrLabel.value = labelize(key);
  const structured = state.addresses?.[key];
  addrInitial.value = structured
    ? {
      line: structured.street01 || '',
      town: structured.city || '',
      state: structured.state || '',
      postcode: structured.post_code || '',
      country: structured.country || '',
    }
    : { line: state.values[key] || '' };
  addrModal.value = true;
}
function putStructuredAddress(key: string, a: { line: string; town: string; state: string; postcode: string; country: string }) {
  if (!key) return;
  if (!state.addresses) state.addresses = {};
  const parts = toBoAppendixAddressParts(a);
  state.addresses[key] = parts;
  state.values[key] = composeBoAppendixAddressLine(parts);
}
function applyAddressTo(key: string, a: { line: string; town: string; state: string; postcode: string; country: string }) {
  putStructuredAddress(key, a);
}
function applyAddress(a: { line: string; town: string; state: string; postcode: string; country: string }) {
  applyAddressTo(addrKey.value, a);
}
watch(() => props.appliedAddress, (a) => { if (a?.key) applyAddressTo(a.key, a); });

function signersOf(varKey: string): SignerEntry[] { return state.signers[varKey] || []; }
function setSigners(varKey: string, list: SignerEntry[]) {
  state.signers[varKey] = Array.isArray(list) ? list : [];
}

function clearBoCriteriaFields(docCode: BoAppendixDocCode = 'BA01') {
  state.criteriaByDoc[docCode] = emptyBoAppendixCriteria();
  const p = docKeyPrefix(docCode);
  for (const k of BO_PCT) {
    state.values[`${p}${k}`] = '';
  }
}

function clearBoParticularsAndCriteria(docCode: BoAppendixDocCode = 'BA01') {
  const p = docKeyPrefix(docCode);
  for (const k of BO_PARTICULARS) {
    state.values[`${p}${k}`] = '';
  }
  state.values[`${p}bo_contact_cd`] = '';
  clearBoCriteriaFields(docCode);
}

/** Person Lookup / BO 행에서 고른 contactCd — Register Detail 이 "linked" 로 판정하도록 보존. */
function resolvePrimaryContactCd(): string {
  const vals = state.values || {};
  const preferred = [
    'bo_contact_cd',
    'ba02_bo_contact_cd',
    'ba03_bo_contact_cd',
    'ba02_member_contact_cd',
    'ba04_member_contact_cd',
    'ba04_sm_contact_cd',
    'ba05_sm_contact_cd',
    'sm_contact_cd',
    'member_contact_cd',
  ];
  for (const k of preferred) {
    const v = String(vals[k] || '').trim();
    if (v) return v;
  }
  for (const k of Object.keys(vals)) {
    if (/_contact_cd$/i.test(k)) {
      const v = String(vals[k] || '').trim();
      if (v) return v;
    }
  }
  return '';
}

function findCachedBoForPerson(a: any): Record<string, unknown> | null {
  const cc = String(a?.contactCd || a?.CONTACT_CD || a?.contact_cd || '').trim();
  const nameNorm = String(
    a?.name || a?.contactName || [a?.firstName, a?.middleName, a?.surname].filter(Boolean).join(' ') || '',
  ).trim().toLowerCase();
  const list = cachedBos.value || [];
  if (cc) {
    const byCd = list.find((b) => String(b.contactCd || b.CONTACT_CD || '').trim() === cc);
    if (byCd) return byCd;
  }
  if (nameNorm) {
    const byName = list.find((b) => {
      const bn = String(b.fullName || b.name || b.contactName || '').trim().toLowerCase();
      return bn && bn === nameNorm;
    });
    if (byName) return byName;
  }
  return null;
}

/** Person Lookup 대상이 법인인지 — CATEGORY_CD(B09001) / isCompany / Appendix B Corporate(ba02_) */
function isCorporatePerson(a: any, fieldKey?: string): boolean {
  const cat = String(a?.categoryCd || a?.CATEGORY_CD || a?.contactCategoryCd || '').trim().toUpperCase();
  if (cat === 'B09001') return true;
  if (cat === 'B09002') return false;
  if (a?.isCompany === true || a?.IsCompany === true || a?.IsCompany === 1 || a?.IsCompany === '1') return true;
  // Appendix B Corporate BO 필드 — lookup 이 corporate member 로 제한됨
  if (fieldKey && /^ba02_bo_/i.test(fieldKey)) return true;
  return false;
}

async function applyPersonToField(key: string, a: any) {
  const base = stripDocKeyPrefix(key);
  const isBa01Bo = base === 'bo_full_name' && !/^ba0[2-5]_/i.test(key);
  const pickedContactCd = String(a?.contactCd || a?.CONTACT_CD || a?.contact_cd || '').trim();

  // Appendix A 만: 기존 BO 레지스터 매핑. Appendix B 는 신규 지정 → Criteria 바인딩 안 함.
  if (isBa01Bo) {
    const matchedBo = findCachedBoForPerson(a);
    if (matchedBo && isIndividualBoRow(matchedBo)) {
      await applyBoParticularsFromRow(matchedBo, true, 'BA01');
      return;
    }
    clearBoCriteriaFields('BA01');
  }

  const name = a.name || a.contactName || [a.firstName, a.middleName, a.surname].filter(Boolean).join(' ') || '';
  state.values[key] = name;

  // Register Detail / registerFromContact 용 — Person Lookup contactCd 보존
  if (base === 'bo_full_name') state.values[relatedKey(key, 'bo_contact_cd')] = pickedContactCd;
  else if (base === 'sm_full_name') state.values[relatedKey(key, 'sm_contact_cd')] = pickedContactCd;
  else if (base === 'member_name') state.values[relatedKey(key, 'member_contact_cd')] = pickedContactCd;

  const addrs = Array.isArray(a.addresses) ? a.addresses : [];
  const addrFromArr = (x: any) => flatAddr(x?.composed || x?.value || x?.address || '');
  const addrKind = (x: any) => [
    x?.type, x?.addressType, x?.addressTypeName, x?.label, x?.originalLabel,
  ].map((s) => String(s || '')).join(' ');
  const isResidential = (x: any) => /residential|registered|home/i.test(addrKind(x));

  const fullAddr = a.residentialAddress || a.primaryAddress || '';
  const res = a.myResidential && !isEmptyMyAddress(a.myResidential) ? a.myResidential : parseMalaysianAddress(fullAddr);
  const fromParsed = !isEmptyMyAddress(res)
    ? composeBoAppendixAddressLine(toBoAppendixAddressParts({
      street: res.street,
      town: res.town,
      state: res.state,
      postcode: res.postcode,
      country: res.country,
    }))
    : '';
  const addrLine = fromParsed
    || flatAddr(fullAddr)
    || addrFromArr(addrs.find(isResidential))
    || addrFromArr(addrs[0])
    || flatAddr(a.address || '');

  if (addrLine) {
    if (base === 'bo_full_name') {
      const addrKeyRes = relatedKey(key, 'bo_residential_address');
      state.values[addrKeyRes] = addrLine;
      if (!state.addresses) state.addresses = {};
      if (!isEmptyMyAddress(res)) {
        state.addresses[addrKeyRes] = toBoAppendixAddressParts({
          street: res.street,
          town: res.town,
          state: res.state,
          postcode: res.postcode,
          country: res.country,
        });
      } else {
        state.addresses[addrKeyRes] = toBoAppendixAddressParts({ line: addrLine });
      }
    }
    if (base === 'sm_full_name') {
      // Appendix C — Address of usual place of residence → residential
      const addrKeyRes = relatedKey(key, 'sm_residential_address');
      state.values[addrKeyRes] = addrLine;
      if (!state.addresses) state.addresses = {};
      if (!isEmptyMyAddress(res)) {
        state.addresses[addrKeyRes] = toBoAppendixAddressParts({
          street: res.street,
          town: res.town,
          state: res.state,
          postcode: res.postcode,
          country: res.country,
        });
      } else {
        state.addresses[addrKeyRes] = toBoAppendixAddressParts({ line: addrLine });
      }
    }
  }

  const idNo = String(a.nric || a.passportNo || a.identificationNo || a.contactRegNo || '').trim();
  if (idNo) {
    if (base === 'bo_full_name') state.values[relatedKey(key, 'bo_id_no')] = idNo;
    if (base === 'sm_full_name') state.values[relatedKey(key, 'sm_id_no')] = idNo;
    if (base === 'member_name') state.values[relatedKey(key, 'member_registration_no')] = idNo;
  }

  // 추가 인적 (BO particulars — 문서별 scope)
  if (base === 'bo_full_name') {
    const email = String(a.email || a.contactEmail || '').trim();
    const nationality = resolveBoNationality(a.nationalityName || a.nationality || a.nationalityCd);
    const position = String(a.occupation || a.positionName || '').trim();
    if (email) state.values[relatedKey(key, 'bo_email')] = email;
    if (nationality) state.values[relatedKey(key, 'bo_nationality')] = nationality;
    if (position) state.values[relatedKey(key, 'bo_position')] = position;

    // 법인 BO — DOB/Race/Gender 는 개인 전용. 프로필에 값이 있어도 채우지 않고 비움.
    if (isCorporatePerson(a, key)) {
      state.values[relatedKey(key, 'bo_dob')] = '';
      state.values[relatedKey(key, 'bo_race')] = '';
      state.values[relatedKey(key, 'bo_gender')] = '';
    } else {
      const dob = formatBoDateDisplaySafe(a.dateOfBirth || a.dob);
      const race = resolveBoRace(a.race || a.raceText || a.contactProfileRace);
      const gender = resolveBoGender(a.gender || a.sexText || a.isMale);
      if (dob) state.values[relatedKey(key, 'bo_dob')] = dob;
      if (race) state.values[relatedKey(key, 'bo_race')] = race;
      if (gender) state.values[relatedKey(key, 'bo_gender')] = gender;
    }
  }

  // Senior Management 추가 인적 (C Corporate/Individual — ba04_/ba05_ 독립)
  if (base === 'sm_full_name') {
    const email = String(a.email || a.contactEmail || '').trim();
    const nationality = resolveBoNationality(a.nationalityName || a.nationality || a.nationalityCd);
    const dob = formatBoDateDisplaySafe(a.dateOfBirth || a.dob);
    const race = resolveBoRace(a.race || a.raceText || a.contactProfileRace);
    const gender = resolveBoGender(a.gender || a.sexText || a.isMale);
    const position = String(a.occupation || a.positionName || '').trim();
    const phone = String(a.mobileNo || a.phone || a.contactNo || '').trim();
    // Office address → business (residential 과 분리)
    const isBusiness = (x: any) => /business|office|service/i.test(addrKind(x));
    const bizFromArr = addrFromArr(addrs.find(isBusiness));
    const office = bizFromArr
      || flatAddr(a.officeAddress || a.businessAddress || a.alternateAddress || '')
      || '';
    const appt = formatBoDateDisplaySafe(a.appointmentDate);
    if (email) state.values[relatedKey(key, 'sm_email')] = email;
    if (nationality) state.values[relatedKey(key, 'sm_nationality')] = nationality;
    if (dob) state.values[relatedKey(key, 'sm_dob')] = dob;
    if (race) state.values[relatedKey(key, 'sm_race')] = race;
    if (gender) state.values[relatedKey(key, 'sm_gender')] = gender;
    if (position) state.values[relatedKey(key, 'sm_position')] = position;
    if (phone) state.values[relatedKey(key, 'sm_phone')] = phone;
    if (office) {
      const addrKeyBiz = relatedKey(key, 'sm_office_address');
      state.values[addrKeyBiz] = office;
      if (!state.addresses) state.addresses = {};
      state.addresses[addrKeyBiz] = toBoAppendixAddressParts({ line: office });
    }
    if (appt) state.values[relatedKey(key, 'sm_appointment_date')] = appt;
  }

  syncPersonToSigners(key, true);
}

function formatBoDateDisplaySafe(raw: unknown): string {
  return formatBoDateDisplay(raw);
}

watch(() => props.selectedPerson, async (p) => {
  const ctx = props.activePersonContext;
  if (!p || !ctx || ctx.fieldGroup !== 'bo-appendix' || !ctx.key) return;
  await applyPersonToField(ctx.key, p as any);
});

provide(BO_APPENDIX_DOC_CTX, {
  valueOf: (k) => String(state.values[k] ?? ''),
  labelOf: labelize,
  typeOf,
  activeKey,
  activate,
  setFieldValue,
  openAddress,
  entityCd,
  signersOf,
  setSigners,
  pendingPickValue,
  placePick,
  criteriaOf: (docCode) => ensureCriteriaDoc(state, docCode),
  toggleCat: (docCode, key) => {
    const c = ensureCriteriaDoc(state, docCode);
    c.cat[key] = !c.cat[key];
  },
  toggleCrit: (docCode, key) => {
    const c = ensureCriteriaDoc(state, docCode);
    c.crit[key] = !c.crit[key];
  },
  setCriteriaPct: (docCode, pctKey, val) => {
    ensureCriteriaDoc(state, docCode).pct[pctKey] = val;
  },
  comboOptionsOf,
});

/** 캐시: entity BO (person lookup 시 Appendix A 매칭용) */
const cachedBos = ref<Record<string, unknown>[]>([]);

function putValue(k: string, v: unknown, force = false) {
  const s = String(v ?? '').trim();
  if (!s) return;
  if (force || !String(state.values[k] || '').trim()) state.values[k] = s;
}

async function enrichContact(entity: string, contactCd: string): Promise<Record<string, unknown> | null> {
  if (!entity || !contactCd) return null;
  try {
    const detRes: any = await docGenApi.getContactDetail(entity, contactCd, {
      addressScope: 'officer',
      positionCd: 'B30046',
    });
    return (detRes?.data?.data || detRes?.data || detRes || null) as Record<string, unknown> | null;
  } catch (e) {
    console.warn('[BO Letters] getContactDetail failed', contactCd, e);
    return null;
  }
}

/** BO_JSON + CONTACT → 인적사항 / Criteria 채움 (개인·법인 BO). docCode 별 필드·criteria 분리. */
async function applyBoParticularsFromRow(
  row: Record<string, unknown>,
  force = false,
  docCode: BoAppendixDocCode = 'BA01',
) {
  if (!row) return;
  const p = docKeyPrefix(docCode);
  const cc = String(row.contactCd || row.CONTACT_CD || '').trim();
  if (cc) state.values[`${p}bo_contact_cd`] = cc;
  const contact = cc ? await enrichContact(entityCd.value, cc) : null;
  const fields = mapBoRowToAppendixParticulars(row, contact);

  // 법인 BO 는 DOB/Race/Gender/NRIC 개인필드를 강제 비움 (잔상 방지)
  const corporate = isCorporateBoRow(row);
  if (corporate) {
    fields.bo_dob = '';
    fields.bo_race = '';
    fields.bo_gender = '';
    // id 는 회사등록번호만
    fields.bo_id_no = String(
      (parseBoJsonPayload(row).whoIs as any)?.PassportNo
      || row.regNo || row.contactRegNo || fields.bo_id_no || '',
    ).trim();
  }

  const jsonForForce = parseBoJsonPayload(row);
  const untilForForce = (jsonForForce.untilDates && typeof jsonForForce.untilDates === 'object'
    ? jsonForForce.untilDates
    : {}) as Record<string, unknown>;
  for (const [k, v] of Object.entries(fields)) {
    const sk = `${p}${k}`;
    if (corporate && ['bo_dob', 'bo_race', 'bo_gender'].includes(k)) {
      if (force) state.values[sk] = '';
      continue;
    }
    const forceField = force
      || (k === 'bo_id_no' && !!v)
      || (k === 'bo_date_became' && !!untilForForce.BOBecomeDate);
    putValue(sk, v, forceField);
  }
  if (corporate && force) {
    state.values[`${p}bo_dob`] = '';
    state.values[`${p}bo_race`] = '';
    state.values[`${p}bo_gender`] = '';
  }

  const { criteria, percents } = mapBoRowToCriteriaAndPercents(row);
  if (import.meta.env.DEV) {
    console.info('[BO Letters] mapped criteria/pct', {
      docCode,
      name: (row as any).fullName || (row as any).name,
      cat: criteria.cat,
      crit: criteria.crit,
      pct: criteria.pct,
      percents,
    });
  }
  const mappedCrit = Object.values(criteria.crit).some(Boolean);
  const mappedPct = Object.values(criteria.pct).some((v) => !!String(v || '').trim());
  const mappedCat = Object.values(criteria.cat).some(Boolean);
  if (force || mappedCrit || mappedPct || mappedCat) {
    state.criteriaByDoc[docCode] = {
      cat: { ...criteria.cat },
      crit: { ...criteria.crit },
      pct: { ...emptyBoAppendixCriteria().pct, ...criteria.pct },
    };
  }
  for (const [k, v] of Object.entries(percents)) {
    const s = String(v ?? '').trim();
    if (s) state.values[`${p}${k}`] = s;
  }
  const critState = ensureCriteriaDoc(state, docCode);
  const pct = critState.pct;
  if (pct?.direct_shares) state.values[`${p}pct_direct_shares`] = pct.direct_shares;
  if (pct?.direct_voting) state.values[`${p}pct_direct_voting`] = pct.direct_voting;
  if (pct?.indirect_shares) state.values[`${p}pct_indirect_shares`] = pct.indirect_shares;
  if (pct?.indirect_voting) state.values[`${p}pct_indirect_voting`] = pct.indirect_voting;
  if (pct?.combined_shares) state.values[`${p}pct_combined_shares`] = pct.combined_shares;
  if (pct?.combined_voting) state.values[`${p}pct_combined_voting`] = pct.combined_voting;

  if (state.values[`${p}bo_full_name`]) syncPersonToSigners(`${p}bo_full_name`, force);
}

/**
 * 로드/설문 시 인물·싸이너 임의 자동지정 없음 (누구를 넣을지 모름).
 * force(엔티티 전환·설문 완료) 일 때만 해당 문서 잔상 필드를 비운다.
 * 채움은 사용자가 person lookup / 직접 입력할 때만.
 */
async function applyAppendixPrefill(force = false) {
  if (!force) return;
  const codes = includedDocCodes.value;
  if (!codes.length) return;

  if (codes.includes('BA01')) {
    clearBoParticularsAndCriteria('BA01');
    state.signers.BA01 = [];
    state.values.signer_designation_BA01 = '';
  }
  if (codes.includes('BA02')) {
    clearBoParticularsAndCriteria('BA02');
    state.values.ba02_member_name = '';
    state.values.ba02_member_registration_no = '';
    state.values.ba02_member_contact_cd = '';
    state.signers.BA02 = [];
    state.values.signer_designation_BA02 = '';
  }
  if (codes.includes('BA03')) {
    clearBoParticularsAndCriteria('BA03');
    state.signers.BA03 = [];
    state.values.signer_designation_BA03 = '';
  }
  if (codes.includes('BA04')) {
    clearSmParticulars('ba04_');
    state.values.ba04_member_name = '';
    state.values.ba04_member_registration_no = '';
    state.signers.BA04 = [];
    state.values.signer_designation_BA04 = '';
  }
  if (codes.includes('BA05')) {
    clearSmParticulars('ba05_');
    state.signers.BA05 = [];
    state.values.signer_designation_BA05 = '';
  }
}

function clearSmParticulars(prefix: 'ba04_' | 'ba05_') {
  for (const k of SM_PARTICULARS) state.values[`${prefix}${k}`] = '';
  state.values[`${prefix}sm_contact_cd`] = '';
  state.values[`${prefix}sm_reason_id`] = '';
  if (prefix === 'ba04_') state.values.ba04_member_contact_cd = '';
  if (prefix === 'ba05_') state.values.ba05_sm_reason = '';
}

const entityPrefilled = ref('');
watch(entityCd, async (cd) => {
  if (!cd || cd === entityPrefilled.value) return;
  const isEntitySwitch = !!entityPrefilled.value && entityPrefilled.value !== cd;
  entityPrefilled.value = cd;
  const force = isEntitySwitch;

  cachedBos.value = [];

  try {
    // person lookup 시 Appendix A 매칭용 (자동채움에는 사용하지 않음)
    const boRes: any = await docGenApi.getBeneficialOwners(cd);
    const bos: any[] = boRes?.data?.beneficialOwners
      || boRes?.data?.data?.beneficialOwners
      || boRes?.data?.beneficial_owners
      || boRes?.data?.list
      || boRes?.beneficialOwners
      || (Array.isArray(boRes?.data) ? boRes.data : []);
    cachedBos.value = Array.isArray(bos) ? bos : [];
  } catch (e) {
    console.warn('[BO Letters] getBeneficialOwners failed', e);
    cachedBos.value = [];
  }

  if (state.survey?.completed) {
    await applyAppendixPrefill(force);
  }
}, { immediate: true });

const includedDocCodes = computed(() => resolveIncludedAppendixCodes(state.survey));

watch(includedDocCodes, async () => {
  if (state.survey?.completed) await applyAppendixPrefill(false);
});

watch(() => state.survey?.completed, async (done) => {
  if (done) await applyAppendixPrefill(true);
});

const overallProgress = computed(() => {
  if (!state.survey?.completed) return 0;
  const codes = includedDocCodes.value.length ? includedDocCodes.value : BO_APPENDIX_DOC_CODES;
  const keys = new Set<string>();
  for (const c of codes) {
    BO_APPENDIX_DOCS[c].keys.forEach((k) => { if (!isOptional(k)) keys.add(k); });
  }
  const list = [...keys];
  if (!list.length) return 0;
  const filled = list.filter((k) => String(state.values[k] || '').trim()).length;
  return Math.round((filled / list.length) * 100);
});
watch(overallProgress, (v) => emit('progress-change', v), { immediate: true });

function onSurveyUpdate(v: BoAppendixSurvey) {
  state.survey = normalizeAppendixSurvey(v);
  state.survey.completed = !!v.completed;
}

const editMode = computed(() => !!props.editTaskId);
const taskConfigSteps = ref<string[]>([]);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });
onMounted(async () => {
  const code = props.selection?.task?.TASK_CODE;
  if (code) {
    try {
      const cfg = await getTaskConfigByCode(code);
      taskConfigSteps.value = (cfg as any)?.processSteps || [];
    } catch { /* noop */ }
  }
});

const availableTemplates = computed(() => (props.selection?.documents || []).filter((d: any) => d.tmpId));

const NAME_KEYWORDS: Record<BoAppendixDocCode, RegExp> = {
  BA01: /appendix\s*a(?!\s*[bc])|particulars\s*of\s*beneficial\s*owner/i,
  BA02: /appendix\s*b.*corporate|others\s*as\s*bo.*corporate|corporate.*appendix\s*b/i,
  BA03: /appendix\s*b.*individual|others\s*as\s*bo.*individual|individual.*appendix\s*b/i,
  BA04: /appendix\s*c.*corporate|senior\s*management.*corporate|corporate.*appendix\s*c/i,
  BA05: /appendix\s*c.*individual|senior\s*management.*individual|individual.*appendix\s*c/i,
};
function tmplName(d: any): string {
  return String(d.templateName || d.docNm || d.name || d.fileName || d.orgFileName || d.docCode || '');
}
function templateFileLabel(d: any, fallback: string): string {
  const raw = tmplName(d).trim() || fallback;
  return raw.replace(/\.docx$/i, '').trim() || fallback;
}
function resolveTemplate(docCode: BoAppendixDocCode): { tmpId: number; label: string } | null {
  const docs = availableTemplates.value as any[];
  const byCode = docs.find((d) => String(d.docCode || '').toUpperCase() === docCode);
  if (byCode?.tmpId) {
    return { tmpId: byCode.tmpId, label: templateFileLabel(byCode, BO_APPENDIX_DOCS[docCode].label) };
  }
  const re = NAME_KEYWORDS[docCode];
  const byName = docs.find((d) => re.test(tmplName(d)));
  if (byName?.tmpId) {
    return { tmpId: byName.tmpId, label: templateFileLabel(byName, BO_APPENDIX_DOCS[docCode].label) };
  }
  return null;
}

async function handleCreateTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  if (!state.survey?.completed || !includedDocCodes.value.length) {
    progress.value = 'Please complete the appendix selection survey first.';
    return;
  }

  const targetCodes = includedDocCodes.value;
  const resolved = targetCodes
    .map((c) => {
      const hit = resolveTemplate(c);
      return hit ? { c, tmpId: hit.tmpId, label: hit.label } : null;
    })
    .filter((r): r is { c: BoAppendixDocCode; tmpId: number; label: string } => !!r);

  if (resolved.length === 0) {
    const avail = (availableTemplates.value as any[]).map((x) => x.templateName || x.docCode).join(', ') || '(none)';
    progress.value = `⚠ 선택한 Appendix에 연결된 문서 템플릿이 없습니다. 관리자에서 BO Letters(Appendix) 문서 템플릿을 연결해 주세요. 선택: [${targetCodes.join(', ')}] · 연결됨: [${avail}]`;
    console.warn('[BO Letters] no templates for selected docs', targetCodes, props.selection?.documents);
    return;
  }

  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const linkedContactCd = resolvePrimaryContactCd();
    const officerData = {
      entityCd: entityCd.value || null,
      // Register Detail hasLinkedContact / registerFromContact 추출용
      contactCd: linkedContactCd,
      officerNameCd: linkedContactCd,
      presetForm: gatherState(),
      companyName: props.entityData?.companyName || props.entityData?.entityName || '',
      companyRegistrationNo: props.entityData?.registrationNo || '',
      boFullName: state.values.bo_full_name || state.values.ba02_bo_full_name || state.values.ba03_bo_full_name || '',
      memberName: state.values.ba02_member_name || state.values.ba04_member_name || '',
      smFullName: state.values.ba05_sm_full_name || state.values.ba04_sm_full_name || '',
      smReasonId: pickSmReasonIdFromState(),
      survey: JSON.parse(JSON.stringify(state.survey)),
      includedDocs: includedDocCodes.value.slice(),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd: props.selection.task.TASK_CODE,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value, taskFlow, officerData,
        }),
      }).then((r) => r.json());
      taskId = taskRes?.data?.taskId || taskRes?.taskId;
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false; return;
      }
    }

    if (resolved.length) {
      progress.value = 'Preparing documents…';
      const formData = buildBoAppendixVariables(state);
      const docsToSave = resolved.map(({ c, tmpId, label }) => {
        const docSigners = state.signers[c] || [];
        const p = docKeyPrefix(c);
        const personName = String(
          state.values[`${p}bo_full_name`]
          || state.values[`${p}member_name`]
          || state.values[`${p}sm_full_name`]
          || 'Company',
        );
        return {
          templateId: tmpId,
          label,
          personName,
          entityCd: entityCd.value || undefined,
          // 문서별 prefix 해제 + criteria(A/B) 또는 SM reason(C) 체크 글리프
          formData: {
            ...adaptFormDataForDoc(c, formData),
            ...((c === 'BA04' || c === 'BA05')
              ? smReasonFlagsFromId(state.values[`${p}sm_reason_id`] || state.values.sm_reason_id)
              : criteriaFlagsForDoc(state.criteriaByDoc?.[c])),
          },
          signers: docSigners.length ? docSigners : undefined,
        };
      });
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((r) => r.json());
        if (!saveRes?.success) progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
      } catch (err: any) {
        console.warn('[BO Letters] document save failed:', err);
      }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }
    try {
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(`presetTaskDetailPrefetch:${taskId}`, JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }));
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[BO Letters] create task 실패', e);
    progress.value = `Error: ${e?.message || e}`;
  } finally {
    creating.value = false;
  }
}

function gatherState() {
  return {
    state: JSON.parse(JSON.stringify(state)),
    survey: JSON.parse(JSON.stringify(state.survey)),
    includedDocs: includedDocCodes.value.slice(),
    companyName: props.entityData?.companyName || props.entityData?.entityName || '',
    companyRegistrationNo: props.entityData?.registrationNo || '',
    boFullName: state.values.bo_full_name || state.values.ba02_bo_full_name || state.values.ba03_bo_full_name || '',
    memberName: state.values.ba02_member_name || state.values.ba04_member_name || '',
    smFullName: state.values.ba05_sm_full_name || state.values.ba04_sm_full_name || '',
    smReasonId: pickSmReasonIdFromState(),
  };
}

/** Appendix C Q13 — 1/2/3 만 유효. Individual(ba05) 우선 */
function pickSmReasonIdFromState(): string {
  for (const v of [state.values.ba05_sm_reason_id, state.values.ba04_sm_reason_id, state.values.sm_reason_id]) {
    const n = Number(String(v ?? '').trim());
    if (n === 1 || n === 2 || n === 3) return String(n);
  }
  return '';
}
function restoreState(s: any) {
  const src = s?.state || s?.presetForm?.state;
  if (src && typeof src === 'object') {
    const base = emptyBoAppendixState();
    Object.assign(state, { ...base, ...src });
    state.values = { ...(src.values || {}) };
    state.signers = { ...(src.signers || {}) };
    state.addresses = { ...(src.addresses || {}) };
    state.survey = normalizeAppendixSurvey(src.survey || s?.survey || s?.presetForm?.survey || {});

    // 문서별 criteria 복원 (+ 구버전 단일 criteria → BA01)
    const byDoc: BoAppendixState['criteriaByDoc'] = {
      BA01: emptyBoAppendixCriteria(),
      BA02: emptyBoAppendixCriteria(),
      BA03: emptyBoAppendixCriteria(),
    };
    const mergeCrit = (raw: any) => ({
      cat: { ...emptyBoAppendixCriteria().cat, ...(raw?.cat || {}) },
      crit: { ...emptyBoAppendixCriteria().crit, ...(raw?.crit || {}) },
      pct: { ...emptyBoAppendixCriteria().pct, ...(raw?.pct || {}) },
    });
    if (src.criteriaByDoc && typeof src.criteriaByDoc === 'object') {
      for (const code of ['BA01', 'BA02', 'BA03', 'BA04', 'BA05'] as BoAppendixDocCode[]) {
        if (src.criteriaByDoc[code]) byDoc[code] = mergeCrit(src.criteriaByDoc[code]);
      }
    } else if (src.criteria && typeof src.criteria === 'object') {
      byDoc.BA01 = mergeCrit(src.criteria);
    }
    state.criteriaByDoc = byDoc;

    // 구버전: 공유 member_name / sm_* → prefix 키로 이전
    const v = state.values;
    if (v.member_name && !v.ba02_member_name) v.ba02_member_name = v.member_name;
    if (v.member_registration_no && !v.ba02_member_registration_no) {
      v.ba02_member_registration_no = v.member_registration_no;
    }
    if (v.member_name && !v.ba04_member_name) v.ba04_member_name = v.member_name;
    if (v.member_registration_no && !v.ba04_member_registration_no) {
      v.ba04_member_registration_no = v.member_registration_no;
    }
    for (const k of SM_PARTICULARS) {
      if (v[k] && !v[`ba05_${k}`]) v[`ba05_${k}`] = v[k];
    }
    if (v.sm_reason && !v.ba05_sm_reason) v.ba05_sm_reason = v.sm_reason;

    // criteria.pct 없고 values 에만 pct_* 있는 경우 BA01 복원
    const p1 = state.criteriaByDoc.BA01?.pct;
    if (p1) {
      if (!p1.direct_shares && v.pct_direct_shares) p1.direct_shares = v.pct_direct_shares;
      if (!p1.direct_voting && v.pct_direct_voting) p1.direct_voting = v.pct_direct_voting;
      if (!p1.indirect_shares && v.pct_indirect_shares) p1.indirect_shares = v.pct_indirect_shares;
      if (!p1.indirect_voting && v.pct_indirect_voting) p1.indirect_voting = v.pct_indirect_voting;
      if (!p1.combined_shares && v.pct_combined_shares) p1.combined_shares = v.pct_combined_shares;
      if (!p1.combined_voting && v.pct_combined_voting) p1.combined_voting = v.pct_combined_voting;
    }

    if (src.survey?.completed || s?.survey?.completed || s?.presetForm?.survey?.completed) {
      state.survey.completed = true;
    }
  } else if (s?.survey || s?.presetForm?.survey) {
    state.survey = normalizeAppendixSurvey(s.survey || s.presetForm.survey);
    if ((s.survey || s.presetForm.survey)?.completed) state.survey.completed = true;
  }
  // 복원 후 BO JSON 기준 % / Criteria 보강 (저장된 스냅샷에 pct 누락된 경우)
  if (state.survey?.completed && cachedBos.value.length) {
    void applyAppendixPrefill(false);
  }
}
defineExpose({ gatherState, restoreState, createTask: handleCreateTask });
</script>

<template>
  <div class="otc-panel" :class="{ 'otc-studio': studio }">
    <div v-if="!studio" class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">Beneficial Owner Letters — Appendix A, B &amp; C</div>
      </div>
      <v-btn variant="text" size="small" class="text-none" @click="emit('back')"><v-icon start size="16">mdi-arrow-left</v-icon>Back</v-btn>
    </div>

    <div v-if="!studio" class="co-title-row">
      <span class="ct-label">Task Title</span>
      <v-text-field v-model="titleModel" placeholder="e.g. BO Letters — Appendix A/B" variant="outlined" density="compact" hide-details class="ct-input" />
    </div>

    <div v-if="!studio" class="co-entity">
      <v-icon size="18" color="primary">mdi-domain</v-icon>
      <span>{{ entityData?.companyName || entityData?.entityName || 'Entity' }}</span>
      <v-btn variant="tonal" size="x-small" class="text-none ml-auto" @click="emit('change-entity')">Change</v-btn>
    </div>

    <div v-if="inputKey" class="co-inputbar">
      <div class="lbl">{{ labelize(inputKey) }} <span v-if="typeOf(inputKey) === 'date'" class="opt">(optional)</span></div>
      <div class="row">
        <v-menu v-if="typeOf(inputKey) === 'date'" v-model="dateMenu" :close-on-content-click="false">
          <template #activator="{ props: mp }">
            <v-text-field v-bind="mp" v-model="inputText" placeholder="DD/MM/YYYY" variant="outlined" density="compact" hide-details append-inner-icon="mdi-calendar" />
          </template>
          <v-date-picker @update:model-value="onPickDate" />
        </v-menu>
        <v-text-field v-else v-model="inputText" :placeholder="labelize(inputKey)" variant="outlined" density="compact" hide-details autofocus @keydown.enter="commitInput" />
        <v-btn color="primary" size="small" variant="flat" @click="commitInput">Set</v-btn>
        <v-btn v-if="typeOf(inputKey) === 'date'" variant="text" size="small" @click="skipInput">Skip</v-btn>
      </div>
    </div>

    <div class="co-doc-body">
      <BoAppendixPackageDoc
        :studio="!!studio"
        :edit-mode="editMode"
        :creating="creating"
        :survey="state.survey"
        @update:survey="onSurveyUpdate"
        @create-task="handleCreateTask"
      />
    </div>

    <div v-if="!studio || progress" class="co-footer">
      <v-btn v-if="!studio" variant="outlined" class="text-none" @click="emit('back')">Back</v-btn>
      <div class="co-foot-right">
        <span v-if="progress" class="co-progress" :class="{ err: progress.startsWith('Error') || progress.includes('⚠') }">{{ progress }}</span>
        <v-btn
          v-if="!studio"
          color="primary"
          class="text-none"
          :loading="creating"
          :disabled="creating || !state.survey?.completed || !includedDocCodes.length"
          :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'"
          @click="handleCreateTask"
        >{{ editMode ? 'Update Task' : 'Create Task' }}</v-btn>
      </div>
    </div>

    <AddressInputModal v-model="addrModal" :label="addrLabel" :has-components="true" :initial="addrInitial" @apply="applyAddress" />

  </div>
</template>

<style scoped>
.otc-panel { display:flex; flex-direction:column; }
.otc-studio { flex:0 0 auto; width:min(1000px, 100%); background:transparent; }
.otc-studio .co-doc-body { background:transparent; padding:0; }
.co-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #eef0f6; }
.co-title { font-weight:700; font-size:15px; } .co-subtitle { font-size:12px; color:#8a94a6; }
.co-title-row { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #eef0f6; }
.co-title-row .ct-label { font-size:12px; font-weight:700; color:#5b6472; white-space:nowrap; }
.co-title-row .ct-input { max-width:520px; }
.co-entity { display:flex; align-items:center; gap:8px; background:#f0f5ff; padding:8px 14px; font-size:13px; }
.co-inputbar { padding:10px 14px; background:#fafbfe; border-bottom:1px solid #eef0f6; position:sticky; top:0; z-index:5; }
.co-inputbar .lbl { font-size:12px; font-weight:700; color:#1B84FF; margin-bottom:6px; } .co-inputbar .opt { color:#8a94a6; font-weight:400; }
.co-inputbar .row { display:flex; gap:8px; align-items:center; }
.co-doc-body { padding:16px 12px 28px; }
.co-footer { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-top:1px solid #eef0f6; gap:12px; }
.co-foot-right { display:flex; align-items:center; gap:12px; margin-left:auto; }
.co-progress { font-size:12px; color:#5b6472; } .co-progress.err { color:#c0392b; }
</style>
